import { auth } from './auth/state.js';

export interface Route {
  /**
   * Either an exact path ("/profile") or a prefix pattern ending in "/:*"
   * for parameterised tails (e.g. "/room/:*" matches "/room/abc123" with
   * the argument "abc123").
   */
  path: string;
  /**
   * Render the route. The second argument receives the captured tail when
   * the route was parameterised (otherwise an empty string).
   */
  render: (container: HTMLElement, pathArg: string) => void | Promise<void>;
  /** Redirect to /login if user is not authenticated. */
  requireAuth?: boolean;
  /** Redirect away (to /) if user IS authenticated. Used for /login + /signup. */
  guestOnly?: boolean;
}

/**
 * History-API router (clean URLs, no `#`).
 *
 *   • Matches `location.pathname` (e.g. `/profile`, `/room/abc123`).
 *   • Uses `history.pushState` to navigate without a full page reload.
 *   • Listens to `popstate` so the browser back/forward buttons work.
 *   • Captures any `<a href="/…">` click globally and routes it through
 *     `navigate()` so legacy anchor links never trigger a hard reload.
 *
 * Server-side requirement: nginx must serve `index.html` for any path
 * that doesn't match a real file (SPA fallback) — already configured
 * via `try_files $uri $uri/ /index.html;`.
 */
export class Router {
  private routes = new Map<string, Route>();
  private container: HTMLElement;
  private current: string | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  add(route: Route): this {
    this.routes.set(route.path, route);
    return this;
  }

  start(): void {
    // Browser back / forward → re-dispatch on the new location.
    window.addEventListener('popstate', () => void this.dispatch());
    // Auth flips (login / logout) → re-evaluate guards against current path.
    auth.subscribe(() => void this.dispatch());
    // Global anchor capture: any `<a href="/whatever">` click is routed
    // through the SPA instead of triggering a full page load. We bail
    // on modifier-clicks (Cmd/Ctrl/Shift) and target=_blank so the
    // browser keeps its normal "open in new tab" behaviour.
    document.addEventListener('click', (e) => this.maybeInterceptAnchor(e));
    // Migrate any legacy `#/path` URLs (bookmarks from the hash era)
    // to the equivalent clean path so old links still land somewhere.
    if (window.location.hash.startsWith('#/')) {
      const legacyPath = window.location.hash.slice(1);
      window.history.replaceState(null, '', legacyPath);
    }
    void this.dispatch();
  }

  /** Programmatic navigation. Always call this rather than mutating
   *  `location.pathname` / `location.hash` directly. */
  navigate(path: string): void {
    if (window.location.pathname === path) {
      // Same URL — re-dispatch to force re-render (e.g. after action).
      void this.dispatch();
      return;
    }
    window.history.pushState(null, '', path);
    void this.dispatch();
  }

  private maybeInterceptAnchor(e: MouseEvent): void {
    // Ignore non-primary clicks + modifier keys (let the browser
    // handle "open in new tab", "save link as", middle-click, etc.).
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    // Only same-origin links without a target → SPA route.
    if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    if (href.startsWith('http://') || href.startsWith('https://')) {
      // Different origin or absolute URL — let the browser handle it.
      try {
        const u = new URL(href);
        if (u.origin !== window.location.origin) return;
        e.preventDefault();
        this.navigate(u.pathname + u.search);
        return;
      } catch { return; }
    }
    if (!href.startsWith('/')) return;          // ignore mailto:, #anchor, …
    e.preventDefault();
    this.navigate(href);
  }

  private async dispatch(): Promise<void> {
    const path = window.location.pathname || '/';
    // Try exact match first, then prefix match for parameterised routes (e.g. /room/abc).
    let route = this.routes.get(path);
    let pathArg = '';
    if (!route) {
      for (const [pattern, r] of this.routes) {
        if (pattern.endsWith('/:*') && path.startsWith(pattern.slice(0, -2))) {
          route = r;
          pathArg = path.slice(pattern.slice(0, -2).length);
          break;
        }
      }
    }
    route = route ?? this.routes.get('/');
    if (!route) return;

    if (route.requireAuth && !auth.user) {
      this.navigate('/login');
      return;
    }
    if (route.guestOnly && auth.user) {
      this.navigate('/');
      return;
    }

    if (this.current !== path) {
      this.current = path;
      // Fresh navigation → pull the latest profile so screens that read
      // from `auth.user` (profile, leaderboard, hub) display up-to-date
      // ranked points / stats. We use the SILENT variant so setting
      // `auth.user` doesn't fire the `auth.subscribe(() => dispatch())`
      // listener — otherwise dispatch() would re-enter itself in a loop
      // and interrupt the very render it kicked off (this is what broke
      // the create-game + tutorial buttons).
      //
      // Skip the refresh on /room/* navigation: those flows are
      // latency-sensitive (joining a Colyseus room) and the in-game
      // HUD reads its data from the room state, not auth.user.
      if (auth.user && !path.startsWith('/room')) {
        try { await auth.refreshSilent(); }
        catch { /* keep cached user */ }
      }
    }
    await route.render(this.container, pathArg);
  }
}
